namespace $.$$ {

	const grab_slack = 8

	const click_slack = 4

	const scene_root = '$' + 'bog_vmap_scene'

	const inner_chained = ( path: readonly string[], chain: readonly string[] )=> {

		let at = 0

		for( const step of path ) {
			if( step === chain[ at ] ) at ++
			if( at === chain.length ) return true
		}

		return at === chain.length
	}

	export type $bog_vmap_app_pane_link_new =
		Pick< $bog_vmap_lang_link, 'from' | 'from_prop' | 'to' | 'to_prop' >
		& { readonly bidi?: boolean }

	export type $bog_vmap_app_pane_link_end = Pick< $bog_vmap_lang_link, 'to' | 'to_prop' >

	export type $bog_vmap_app_pane_tree_move = {
		readonly names: readonly string[]
		readonly owner: string
		readonly index: number
	}

	export type $bog_vmap_app_pane_clone = {
		readonly names: readonly string[]
		readonly spots?: { readonly [ name: string ]: { readonly x: number, readonly y: number } }
		readonly owner?: string
		readonly index?: number
	}

	export type $bog_vmap_app_pane_carry = {
		readonly x: number
		readonly y: number
		readonly owner: string
		readonly index: number
	}

	export type $bog_vmap_app_pane_files = {
		readonly files: readonly File[]
		readonly x: number
		readonly y: number
		readonly owner: string
		readonly index: number
	}

	export type $bog_vmap_app_pane_peer = {
		postMessage( data: unknown, origin: string ): void
		readonly origin: string
	}

	export type $bog_vmap_app_pane_tool = 'select' | 'board' | 'text' | 'hand'

	export type $bog_vmap_app_text_born = {
		readonly x: number
		readonly y: number
		readonly width: number
		readonly text: string
	}

	export type $bog_vmap_app_pane_camera_kept = {
		readonly x: number
		readonly y: number
		readonly zoom: number
	}

	export type $bog_vmap_app_pane_stroke = {
		readonly key: string
		readonly code: string
		readonly altKey: boolean
		readonly ctrlKey: boolean
		readonly metaKey: boolean
		readonly shiftKey: boolean
		readonly target: EventTarget | null
		preventDefault(): void
	}

	export type $bog_vmap_app_pane_draft = {
		readonly from: readonly [ number, number ]
		readonly to: readonly [ number, number ]
	}

	export type $bog_vmap_app_pane_size = {
		readonly name: string
		readonly width: number
		readonly height: number
		readonly floor: boolean
	}

	export type $bog_vmap_app_pane_sizing = {
		readonly name: string
		readonly corner: string
		readonly box: $bog_vmap_bridge_rect
		readonly grab: readonly [ number, number ]
		readonly to: readonly [ number, number ]
		readonly ratio: boolean
	}

	const corners = [ 'nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w' ] as const

	const size_min = 8

	export type $bog_vmap_app_pane_menu = {
		readonly screen: readonly [ number, number ]
		readonly world: readonly [ number, number ]
		readonly name: string | null
	}

	export class $bog_vmap_app_pane extends $.$bog_vmap_app_pane {
		override doc_js(): { readonly [ klass: string ]: string } {
			return {}
		}

		zoom_min() { return .05 }
		zoom_max() { return 16 }

		@ $mol_mem
		override camera_zoom( next?: number ) {
			const zoom = next ?? 1
			return Math.min( this.zoom_max(), Math.max( this.zoom_min(), zoom ) )
		}

		camera(): $bog_vmap_bridge_camera {
			const shift = this.camera_shift()
			const zoom = this.camera_zoom()
			return { x: -shift[0] / zoom, y: -shift[1] / zoom, zoom }
		}

		fit_gap() { return 24 }

		box_union( boxes: readonly $bog_vmap_bridge_rect[] ) {
			let left = Infinity, top = Infinity, right = -Infinity, bottom = -Infinity

			for( const box of boxes ) {
				if( !( box.width > 0 ) || !( box.height > 0 ) ) continue
				left = Math.min( left, box.x )
				top = Math.min( top, box.y )
				right = Math.max( right, box.x + box.width )
				bottom = Math.max( bottom, box.y + box.height )
			}

			if( !( right > left ) || !( bottom > top ) ) return null

			return { x: left, y: top, width: right - left, height: bottom - top }
		}

		@ $mol_action
		override camera_fit( next?: readonly $bog_vmap_bridge_rect[] | null ) {
			return this.fit( next ?? [], 1 )
		}

		@ $mol_action
		picked_fit() {
			return this.fit( this.picked().flatMap( name => this.part_size( name ) ?? [] ), this.zoom_max() )
		}

		@ $mol_action
		override node_show( name?: string | null ) {
			const box = name ? this.part_size( name ) : null
			if( box ) this.fit( [ box ], this.camera_zoom() )
			return null
		}

		fit( boxes: readonly $bog_vmap_bridge_rect[], limit: number ) {
			const box = this.box_union( boxes )
			const rect = this.pane_rect()

			if( !box || !rect.width || !rect.height ) return null

			const gap = this.fit_gap()

			const zoom = this.camera_zoom( Math.min(
				limit,
				Math.max( rect.width - gap * 2, 1 ) / box.width,
				Math.max( rect.height - gap * 2, 1 ) / box.height,
			) )

			this.camera_shift( new this.$.$mol_vector_2d(
				rect.width / 2 - ( box.x + box.width / 2 ) * zoom,
				rect.height / 2 - ( box.y + box.height / 2 ) * zoom,
			) )

			return box
		}

		@ $mol_action
		override camera_reset() {
			if( this.camera_fit( this.free_boxes() ) ) return null

			this.camera_zoom( 1 )
			this.camera_shift( new this.$.$mol_vector_2d( 0, 0 ) )

			return null
		}

		camera_key() {
			const key = this.doc_key()
			return key ? `vmap_camera ${ key }` : ''
		}

		camera_kept( next?: $bog_vmap_app_pane_camera_kept | null ) {
			const key = this.camera_key()
			if( !key ) return null

			return this.$.$mol_state_session.value< $bog_vmap_app_pane_camera_kept | null >( key, next ) ?? null
		}

		@ $mol_mem
		camera_doc( next?: string ) {
			return next ?? ''
		}

		@ $mol_action
		camera_settle() {

			const key = this.camera_key()
			if( !key || key === this.camera_doc() ) return null

			const kept = this.camera_kept()

			if( kept ) {
				this.camera_doc( key )
				this.camera_zoom( kept.zoom )
				this.camera_shift( new this.$.$mol_vector_2d( kept.x, kept.y ) )
				return null
			}

			if( !this.free_boxes().length ) return null

			this.camera_doc( key )
			this.camera_reset()

			return null
		}

		@ $mol_mem
		camera_keep() {

			const key = this.camera_key()
			if( !key || this.camera_doc() !== key ) return null

			const shift = this.camera_shift()

			this.camera_kept({ x: shift[0], y: shift[1], zoom: this.camera_zoom() })

			return null
		}

		override zoom_title() {
			return Math.round( this.camera_zoom() * 100 ) + '%'
		}

		override zoom_in() {
			this.zoom_by( 1.25 )
		}

		override zoom_out() {
			this.zoom_by( 1 / 1.25 )
		}

		@ $mol_action
		override zoom_full() {
			this.zoom_to( 1 )
			return null
		}

		zoom_by( mult: number ) {
			this.zoom_to( this.camera_zoom() * mult )
		}

		zoom_to( next: number ) {
			const zoom_prev = this.camera_zoom()
			const zoom_next = this.camera_zoom( next )
			const real = zoom_next / zoom_prev

			const rect = this.pane_rect()
			const center = new this.$.$mol_vector_2d( rect.width / 2, rect.height / 2 )

			this.camera_shift(
				this.camera_shift().multed0( real ).added1( center.multed0( 1 - real ) )
			)

		}

		override error() {
			return [ this.isolation(), this.error_at( 'compile' ), this.error_at( 'runtime' ) ]
				.filter( Boolean )
				.join( '\n' )
		}

		override pack_note() {
			return this.error_at( 'pack' )
		}

		@ $mol_mem
		errors(): { readonly [ node: string ]: string } {
			const res = {} as { [ node: string ]: string }

			for( const at of [ 'compile', 'runtime' ] as const ) {
				const node = this.error_node( at )
				const text = this.error_at( at )
				if( !node || !text ) continue

				res[ node ] = res[ node ] ? res[ node ] + '\n' + text : text

			}

			return res
		}

		override node_error( name: string ) {
			return this.errors()[ name ] ?? ''
		}

		@ $mol_mem
		override error_marks() {
			return Object.keys( this.errors() )
				.filter( name => this.part_box( name ) )
				.map( name => this.Mark( name ) )
		}

		@ $mol_mem_key
		override mark_hint( name: string ) {
			return this.node_error( name )
		}

		@ $mol_mem_key
		override mark_style( name: string ): { readonly [ prop: string ]: string } {
			const rect = this.part_box( name )
			if( !rect ) return {}

			return {
				left: rect.left + 'px',
				top: rect.top + 'px',
			}
		}

		scene_key() {
			return this.scene_generation() + ' ' + this.pack_uri()
		}

		@ $mol_mem
		override scene_lost( next?: string ) {
			return next ?? ''
		}

		scene_boot_fail() {
			return `parent.postMessage({ns:'${ $bog_vmap_bridge_ns }',kind:'boot_fail'},'*')`
		}

		override scene_html() {
			return [
				'<!doctype html>',
				'<html lang="en" mol_view_root style="height:100%;width:100%;color-scheme:dark">',
				'<head><meta charset="utf-8" />',
				'<meta name="viewport" content="width=device-width, height=device-height, initial-scale=1" />',
				'</head>',
				'<body mol_view_root style="padding:0;margin:0;height:100%;width:100%">',
				`<div mol_view_root="${ scene_root }"></div>`,
				`<script src="${ this.scene_bundle() }" charset="utf-8" onerror="${ this.scene_boot_fail() }"></script>`,
				'</body></html>',
			].join( '' )
		}

		scene_peer(): $bog_vmap_app_pane_peer | null {
			return ( this.Scene( this.scene_key() ).dom_node() as HTMLIFrameElement ).contentWindow
		}

		override sub() {
			return [
				this.Field(),
				... this.ruler_views(),
			] as readonly $mol_view[]
		}

		override field_sub() {
			return [
				... this.scene_shown() ? [ this.Scene( this.scene_key() ) ] : [],
				this.Overlay(),
				this.Wire(),
				this.Values(),
				this.Names(),
				this.Marks(),
				... this.slot() ? [ this.Insert() ] : [],
				... this.sizing() ? [ this.Sizing() ] : [],
				... this.text_edited() ? [ this.Text_field( this.text_edited()! ) ] : [],
				... this.text_spot() ? [ this.Text_new() ] : [],
				... this.say() ? [ this.Say() ] : [],
				... this.band() ? [ this.Band() ] : [],
				... this.draft() ? [ this.Draft() ] : [],
				... this.snap_hint_views(),
				... this.gap_views(),
				... this.guide_views(),
				... this.ghost_views(),
				... this.menu() ? [ this.menu_view() ] : [],
			] as readonly $mol_view[]
		}

		override field_style(): { readonly [ prop: string ]: string } {
			const size = this.ruler_size() + 'px'
			return { left: size, top: size }
		}

		@ $mol_mem
		scene_shown( next?: boolean ) {
			return next ?? true
		}

		remount_delay() {
			return 500
		}

		@ $mol_action
		override scene_restart() {
			this.restart_tries( 0 )
			this.scene_relaunch()
		}

		@ $mol_action
		scene_relaunch() {
			this.scene_generation( this.scene_generation() + 1 )
			this.warmed( false )
			this.stalled( false )
			this.scene_lost( '' )
			this.scene_shown( false )

			new this.$.$mol_after_timeout( this.remount_delay(), ()=> this.scene_shown( true ) )
		}

		@ $mol_mem_key
		override handshake( key: string, next?: number ) {
			return next ?? 0
		}

		override ready() {
			return this.handshake( this.scene_key() ) > 0
		}

		target() {
			return this.ready() ? this.scene_peer() : null
		}

		poke_at = 0

		@ $mol_mem
		answer_at( next?: number ) {
			return next ?? 0
		}

		stamp_last = 0

		stamp() {
			return ++ this.stamp_last
		}

		@ $mol_mem
		poke_direct( next?: number ) {
			return next ?? 0
		}

		@ $mol_mem
		override warmed( next?: boolean ) {
			return next ?? false
		}

		answer_limit() {
			return 8000
		}

		cold_limit() {
			return 30000
		}

		@ $mol_mem
		override stalled( next?: boolean ) {
			return next ?? false
		}

		ping_period() {
			return 2000
		}

		@ $mol_mem
		heartbeat() {
			if( !this.warmed() ) return null

			const target = this.target()
			if( !target ) return null

			this.answer_at()

			return new this.$.$mol_after_timeout( this.ping_period(), () => {
				const nonce = this.stamp()
				this.poke_direct( this.post( target, { kind: 'ping', nonce } ) )
			} )
		}

		@ $mol_mem
		watchdog() {
			this.pack_push()
			this.theme_push()
			this.doc_push()
			this.css_push()
			this.libs_push()
			this.spots_push()
			this.camera_push()

			this.poke_direct()

			const held = !this.warmed() && Boolean( this.pack_note() )

			if( !held && this.poke_at <= this.answer_at() ) return null

			const limit = this.warmed() ? this.answer_limit() : this.cold_limit()

			return new this.$.$mol_after_timeout( limit, () => {

				if( !this.warmed() && !this.pack_note() && this.restart_tries() < this.restart_tries_max() ) {
					this.restart_tries( this.restart_tries() + 1 )
					this.scene_relaunch()
					return
				}

				this.stalled( true )
			} )
		}

		@ $mol_mem
		restart_tries( next?: number ) {
			return next ?? 0
		}

		restart_tries_max() {
			return 1
		}

		@ $mol_mem
		sizes( next?: { readonly [ node: string ]: $bog_vmap_bridge_rect } ) {
			return next ?? {}
		}

		sizes_merged( fresh: { readonly [ node: string ]: $bog_vmap_bridge_rect } ) {

			const prefix = this.doc_root() + '/'
			const leaf = ( key: string )=> key.slice( prefix.length ).split( '/' ).pop() ?? ''

			const live = new Set( this.doc_paths() )
			const moved = new Set( Object.keys( fresh ).map( leaf ) )

			const under = new Set< string >()

			for( const key of Object.keys( fresh ) ) {
				if( !key.startsWith( prefix ) ) continue

				const steps = key.slice( prefix.length ).split( '/' )
				for( let at = 1; at < steps.length; ++ at ) under.add( steps.slice( 0, at ).join( '/' ) )
			}

			const held = ( key: string )=> {

				if( key in fresh ) return true
				if( !live.size ) return !moved.has( leaf( key ) )
				if( !key.startsWith( prefix ) ) return true

				const steps = key.slice( prefix.length ).split( '/' )

				let part = ''

				for( let at = 1; at <= steps.length; ++ at ) {
					const path = steps.slice( 0, at ).join( '/' )
					if( !live.has( path ) ) break
					part = path
				}

				if( !part ) return false
				if( part === steps.join( '/' ) ) return true

				return !under.has( part )
			}

			const kept = {} as { [ node: string ]: $bog_vmap_bridge_rect }
			const sizes = this.sizes()

			for( const key of Object.keys( sizes ) ) {
				if( held( key ) ) kept[ key ] = sizes[ key ]
			}

			return { ... kept, ... fresh }
		}

		@ $mol_mem
		nodes_measured() {
			const prefix = this.doc_root() + '/'
			const known = new Set( this.doc_names() )
			const nodes = [] as { name: string, path: readonly string[], box: $bog_vmap_bridge_rect }[]

			for( const key of Object.keys( this.sizes() ) ) {
				if( !key.startsWith( prefix ) ) continue

				const path = key.slice( prefix.length ).split( '/' )

				if( path.some( step => !known.has( step ) ) ) continue

				nodes.push({ name: path[ path.length - 1 ], path, box: this.sizes()[ key ] })
			}

			return nodes
		}

		@ $mol_mem
		inner_measured() {

			const prefix = this.doc_root() + '/'
			const known = new Set( this.doc_names() )
			const found = [] as {
				readonly path: readonly string[]
				readonly box: $bog_vmap_bridge_rect
			}[]

			for( const key of Object.keys( this.sizes() ) ) {
				if( !key.startsWith( prefix ) ) continue

				const path = key.slice( prefix.length ).split( '/' )
				if( path.length < 2 || !known.has( path[ 0 ] ) ) continue

				found.push({ path, box: this.sizes()[ key ] })
			}

			return found
		}

		inner_size( address: string ) {

			const chain = address.split( '/' )
			const last = chain[ chain.length - 1 ]

			const ends = this.inner_measured().filter( one =>
				one.path[ 0 ] === chain[ 0 ] && one.path[ one.path.length - 1 ] === last
			)

			if( !ends.length ) return null

			const chained = ends.filter( one => inner_chained( one.path, chain ) )
			const kept = chained.length ? chained : ends

			return kept.reduce( ( one, other )=> other.path.length < one.path.length ? other : one ).box
		}

		override part_size( name: string ) {
			if( name.includes( '/' ) ) return this.inner_size( name )

			let found = null as $bog_vmap_bridge_rect | null

			for( const node of this.nodes_measured() ) if( node.name === name ) found = node.box

			return found
		}

		part_names() {
			return this.nodes_measured().map( node => node.name )
		}

		free_names() {
			return this.nodes_measured().filter( node => node.path.length === 1 ).map( node => node.name )
		}

		free_boxes() {
			return this.nodes_measured().filter( node => node.path.length === 1 ).map( node => node.box )
		}

		override world_center(): readonly number[] {
			const rect = this.pane_rect()
			const shift = this.camera_shift()
			const zoom = this.camera_zoom()

			return [
				( rect.width / 2 - shift[0] ) / zoom,
				( rect.height / 2 - shift[1] ) / zoom,
			]
		}

		override free_spot(): readonly number[] {
			const [ x, start ] = this.world_center()
			const boxes = this.nodes_measured().map( node => node.box )

			const covers = ( box: $bog_vmap_bridge_rect, y: number )=> {
				return x >= box.x && x <= box.x + box.width
					&& y >= box.y && y <= box.y + box.height
			}

			let y = start

			for( let step = 0; step <= boxes.length; ++step ) {
				const hit = boxes.find( box => covers( box, y ) )
				if( !hit ) break

				y = hit.y + hit.height + 24
			}

			return [ x, y ]
		}

		node_path( name: string ): readonly string[] {
			for( const node of this.nodes_measured() ) {
				if( node.name === name ) return node.path.slice( 0, -1 )
			}

			return []
		}

		@ $mol_mem
		drag( next?: {
			name: string,
			spots: { readonly [ name: string ]: { readonly x: number, readonly y: number } },
			grab: readonly [ number, number ],
			sizes: { readonly [ node: string ]: $bog_vmap_bridge_rect },
			nested: boolean,
			box: $bog_vmap_bridge_rect | null,
		} | null ) {
			return next ?? null
		}

		snap_slack() {
			return 6
		}

		snap_off( event: PointerEvent ) {
			return Boolean( event.metaKey || event.ctrlKey )
		}

		spot_box( name: string ): $bog_vmap_bridge_rect | null {
			const size = this.part_size( name )
			if( !size ) return null

			const spot = this.spots()[ name ]
			if( !spot ) return size

			return { x: spot.x, y: spot.y, width: size.width, height: size.height }
		}

		snap_boxes( moving: { readonly [ name: string ]: unknown } ) {
			const rect = this.pane_rect()
			const zoom = this.camera_zoom()
			const shift = this.camera_shift()

			const boxes = [] as $bog_vmap_bridge_rect[]

			for( const name of this.free_names() ) {
				if( name in moving ) continue

				const box = this.spot_box( name )
				if( !box || !( box.width > 0 ) || !( box.height > 0 ) ) continue

				const screen = this.$.$bog_vmap_app_pane_screen( box, zoom, shift )
				if( screen.left + screen.width < 0 || screen.top + screen.height < 0 ) continue
				if( screen.left > rect.width || screen.top > rect.height ) continue

				boxes.push( box )
			}

			return boxes
		}

		snap_at( box: $bog_vmap_bridge_rect | null, moving: { readonly [ name: string ]: unknown }, shift: readonly [ number, number ] ) {
			if( !box ) return null

			return this.$.$bog_vmap_app_pane_snap(
				{ x: box.x + shift[0], y: box.y + shift[1], width: box.width, height: box.height },
				this.snap_boxes( moving ),
				this.snap_slack() / this.camera_zoom(),
				this.guide_rails(),
			)
		}

		@ $mol_mem
		snap_hints( next?: readonly $bog_vmap_app_pane_snap_line[] ) {
			return next ?? []
		}

		gap_bounds( name: string ) {

			const holder = this.node_path( name ).at( -2 )
			if( !holder ) return null

			return this.part_size( holder )
		}

		picked_box() {
			const boxes = this.picked().flatMap( name => this.spot_box( name ) ?? [] )
			return boxes.length ? this.box_union( boxes ) : null
		}

		@ $mol_mem
		gaps(): readonly $bog_vmap_app_pane_gap[] {

			const drag = this.drag()

			if( drag ) {

				const box = this.picked_box()
				if( !box ) return []

				return this.$.$bog_vmap_app_pane_gaps(
					box,
					this.snap_boxes( drag.spots ),
					this.gap_bounds( drag.name ),
				)
			}

			const over = this.hovered()
			if( !over ) return []

			const box = this.picked_box()
			const other = this.spot_box( over )
			if( !box || !other ) return []

			return this.$.$bog_vmap_app_pane_gaps( box, [ other ] )
		}

		ruler_size() {
			return this.ruler_shown() ? 20 : 0
		}

		ruler_room() {
			return { width: 480, height: 320 }
		}

		ruler_shown() {
			const rect = this.view_rect()
			const room = this.ruler_room()
			if( !rect ) return false
			return rect.width >= room.width && rect.height >= room.height
		}

		ruler_zero() {
			const inside = this.entered()
			const box = inside ? this.part_size( inside ) : null
			return box ? { x: box.x, y: box.y } : { x: 0, y: 0 }
		}

		@ $mol_mem_key
		ruler_ticks( axis: $bog_vmap_app_pane_snap_axis ): readonly $bog_vmap_app_pane_ruler_tick[] {

			if( !this.ruler_shown() ) return []

			const rect = this.pane_rect()
			const zoom = this.camera_zoom()
			const shift = this.camera_shift()

			const size = axis === 'x' ? rect.width : rect.height
			const start = axis === 'x' ? shift[ 0 ] : shift[ 1 ]

			const step = this.$.$bog_vmap_app_pane_ruler_step( zoom )
			const zero = axis === 'x' ? this.ruler_zero().x : this.ruler_zero().y

			return this.$.$bog_vmap_app_pane_ruler_ticks( -start / zoom, ( size - start ) / zoom, step, zero )
		}

		ruler_span( axis: $bog_vmap_app_pane_snap_axis ) {

			const box = this.picked_box()
			if( !box ) return null

			const zoom = this.camera_zoom()
			const shift = this.camera_shift()

			const from = axis === 'x' ? box.x : box.y
			const size = axis === 'x' ? box.width : box.height
			const start = axis === 'x' ? shift[ 0 ] : shift[ 1 ]

			return { at: from * zoom + start, size: size * zoom }
		}

		ruler_views() {
			return this.ruler_shown() ? [ this.Ruler( 'x' ), this.Ruler( 'y' ) ] : []
		}

		@ $mol_mem_key
		override ruler_sub( axis: string ): readonly $mol_view[] {

			const side = axis as $bog_vmap_app_pane_snap_axis

			return [
				... this.ruler_span( side ) ? [ this.Span( axis ) ] : [],
				... this.ruler_ticks( side ).map( ( tick, index )=> this.Tick( axis + ':' + index ) ),
			]
		}

		@ $mol_mem_key
		override ruler_style( axis: string ): { readonly [ prop: string ]: string } {

			const size = this.ruler_size() + 'px'

			return axis === 'x'
				? { left: size, top: '0', right: '0', height: size }
				: { left: '0', top: size, bottom: '0', width: size }
		}

		override tick_title( key: string ) {
			const [ axis, index ] = key.split( ':' )
			return String( this.ruler_ticks( axis as $bog_vmap_app_pane_snap_axis )[ Number( index ) ]?.label ?? '' )
		}

		@ $mol_mem_key
		override tick_style( key: string ): { readonly [ prop: string ]: string } {

			const [ axis, index ] = key.split( ':' )
			const tick = this.ruler_ticks( axis as $bog_vmap_app_pane_snap_axis )[ Number( index ) ]
			if( !tick ) return {}

			const zoom = this.camera_zoom()
			const shift = this.camera_shift()
			const at = tick.at * zoom + ( axis === 'x' ? shift[ 0 ] : shift[ 1 ] )

			return axis === 'x'
				? { left: at + 'px' }
				: { top: at + 'px' }
		}

		@ $mol_mem_key
		override span_style( axis: string ): { readonly [ prop: string ]: string } {

			const span = this.ruler_span( axis as $bog_vmap_app_pane_snap_axis )
			if( !span ) return {}

			return axis === 'x'
				? { left: span.at + 'px', width: span.size + 'px' }
				: { top: span.at + 'px', height: span.size + 'px' }
		}

		gap_views() {
			return this.gaps().map( ( gap, index )=> this.Gap( index ) )
		}

		override gap_title( index: number ) {
			const gap = this.gaps()[ index ]
			return gap ? String( gap.size ) : ''
		}

		@ $mol_mem_key
		override gap_style( index: number ): { readonly [ prop: string ]: string } {

			const gap = this.gaps()[ index ]
			if( !gap ) return {}

			const middle = ( gap.from + gap.to ) / 2

			const spot = gap.axis === 'x'
				? { x: middle, y: gap.cross, width: 0, height: 0 }
				: { x: gap.cross, y: middle, width: 0, height: 0 }

			const rect = this.$.$bog_vmap_app_pane_screen( spot, this.camera_zoom(), this.camera_shift() )

			return {
				left: rect.left + 'px',
				top: rect.top + 'px',
			}
		}

		snap_hint_views() {
			return this.snap_hints().map( ( line, index )=> this.Snap_hint( index ) )
		}

		@ $mol_mem_key
		override snap_hint_style( index: number ): { readonly [ prop: string ]: string } {
			const line = this.snap_hints()[ index ]
			if( !line ) return {}

			const box = line.axis === 'x'
				? { x: line.at, y: line.from, width: 0, height: line.to - line.from }
				: { x: line.from, y: line.at, width: line.to - line.from, height: 0 }

			const rect = this.$.$bog_vmap_app_pane_screen( box, this.camera_zoom(), this.camera_shift() )

			return {
				left: rect.left + 'px',
				top: rect.top + 'px',
				width: Math.max( rect.width, 1 ) + 'px',
				height: Math.max( rect.height, 1 ) + 'px',
			}
		}

		guide_lines(): { readonly [ id: string ]: $bog_vmap_app_pane_rail } {
			return this.guides() as { readonly [ id: string ]: $bog_vmap_app_pane_rail }
		}

		guide_at( id: string ): $bog_vmap_app_pane_rail | null {
			const drag = this.guide_drag()
			if( drag && drag.id === id ) return { axis: drag.axis, at: drag.at }

			const line = this.guide_lines()[ id ]
			if( !line ) return null
			if( line.axis !== 'x' && line.axis !== 'y' ) return null

			return line
		}

		guide_ids(): readonly string[] {
			const ids = Object.keys( this.guide_lines() ).sort()
			const drag = this.guide_drag()
			if( drag && !ids.includes( drag.id ) ) ids.push( drag.id )
			return ids
		}

		guide_rails(): readonly $bog_vmap_app_pane_rail[] {
			return this.guide_ids().flatMap( id => this.guide_at( id ) ?? [] )
		}

		guide_views() {
			return this.guide_ids().map( id => this.Guide( id ) )
		}

		override guide_picked( id: string ) {
			return this.guide_pick() === id
		}

		override guide_axis( id: string ) {
			return this.guide_at( id )?.axis ?? ''
		}

		@ $mol_mem_key
		override guide_style( id: string ): { readonly [ prop: string ]: string } {
			const line = this.guide_at( id )
			if( !line ) return {}

			const zoom = this.camera_zoom()
			const shift = this.camera_shift()
			const at = line.at * zoom + ( line.axis === 'x' ? shift[0] : shift[1] )

			return line.axis === 'x'
				? { left: at + 'px', top: '0', bottom: '0', width: '1px' }
				: { top: at + 'px', left: '0', right: '0', height: '1px' }
		}

		@ $mol_mem
		guide_pick( next?: string | null ) {
			return next ?? null
		}

		@ $mol_mem
		guide_drag( next?: $bog_vmap_app_pane_guide_drag | null ) {
			return next ?? null
		}

		guide_snap( axis: $bog_vmap_app_pane_snap_axis, at: number ) {
			const slack = this.snap_slack() / this.camera_zoom()
			const stops = this.snap_boxes({}).flatMap(
				box => this.$.$bog_vmap_app_pane_snap_stops( box, axis )
			)

			return at + this.$.$bog_vmap_app_pane_snap_gap( [ at ], stops, slack )
		}

		guide_point( event: { readonly clientX: number, readonly clientY: number }, axis: $bog_vmap_app_pane_snap_axis ) {
			const point = this.world_point( event )
			return this.guide_snap( axis, axis === 'x' ? point[0] : point[1] )
		}

		guide_off( event: { readonly clientX: number, readonly clientY: number }, axis: $bog_vmap_app_pane_snap_axis ) {
			const screen = this.screen_point( event )
			return ( axis === 'x' ? screen[0] : screen[1] ) < 0
		}

		@ $mol_action
		override ruler_press( axis: string, next?: PointerEvent | null ) {
			if( !next || !this.editable() ) return null

			const side = axis as $bog_vmap_app_pane_snap_axis
			if( side !== 'x' && side !== 'y' ) return null

			next.preventDefault()
			this.grab( next )

			const id = this.$.$mol_guid()

			this.guide_pick( id )
			this.guide_drag({ id, axis: side, at: this.guide_point( next, side ), born: true, off: false })

			return null
		}

		@ $mol_action
		override guide_press( id: string, next?: PointerEvent | null ) {
			if( !next ) return null

			next.preventDefault()
			next.stopPropagation()

			this.guide_pick( id )
			if( !this.editable() ) return null

			const line = this.guide_at( id )
			if( !line ) return null

			this.grab( next )
			this.guide_drag({ id, axis: line.axis, at: line.at, born: false, off: false })

			return null
		}

		@ $mol_action
		override guide_move( id: string, next?: PointerEvent | null ) {
			const drag = this.guide_drag()
			if( !drag || !next ) return null

			if( !next.buttons ) {
				const node = next.target as Element | null
				let kept = false
				try { kept = node?.hasPointerCapture?.( next.pointerId ) ?? false } catch {}
				if( !kept ) return this.guide_release( id, next )
			}

			next.preventDefault()

			this.guide_drag({
				... drag,
				at: this.guide_point( next, drag.axis ),
				off: this.guide_off( next, drag.axis ),
			})

			return null
		}

		@ $mol_action
		override guide_release( id: string, next?: PointerEvent | null ) {
			const drag = this.guide_drag()
			if( !drag ) return null

			this.guide_drag( null )

			if( drag.off ) {
				if( !drag.born ) this.guide_drop( drag.id )
				else this.guide_pick( null )
				return null
			}

			this.guides({ ... this.guide_lines(), [ drag.id ]: { axis: drag.axis, at: drag.at } })

			return null
		}

		@ $mol_action
		guide_drop( id: string ) {
			const next = { ... this.guide_lines() } as { [ id: string ]: $bog_vmap_app_pane_rail }
			delete next[ id ]

			this.guides( next )
			if( this.guide_pick() === id ) this.guide_pick( null )

			return null
		}

		grab( event: PointerEvent ) {
			const node = event.target as Element | null
			try { node?.setPointerCapture?.( event.pointerId ) } catch {}
		}

		@ $mol_mem
		press( next?: {
			screen: readonly [ number, number ],
			world: readonly [ number, number ],
			moved: boolean,
			entering: boolean,
			name: string | null,
		} | null ) {
			return next ?? null
		}

		primary() {
			const picked = this.picked()
			return picked.length ? picked[ picked.length - 1 ] : null
		}

		override inside() {
			const name = this.primary()
			return Boolean( name ) && this.entered() === name
		}

		@ $mol_action
		override leave() {
			const was = this.entered() !== null
			this.entered( null )
			if( was ) this.focused( true )
			return null
		}

		enter( name: string | null ) {
			this.entered( name )

			try {
				( this.Scene( this.scene_key() ).dom_node() as HTMLElement ).focus()
			} catch {}
		}

		override hand() {
			return this.tool() === 'hand' || this.grip()
		}

		@ $mol_action
		tool_take( next: $bog_vmap_app_pane_tool ) {
			if( ( next === 'board' || next === 'text' ) && !this.editable() ) return
			if( next === 'text' && !this.text_enabled() ) return

			this.text_new_submit()
			if( next !== 'select' ) this.leave()
			this.draft( null )
			this.tool( next )
		}

		override tool_select( next?: boolean ) {
			if( next !== undefined ) this.tool_take( 'select' )
			return this.tool() === 'select'
		}

		override tool_board( next?: boolean ) {
			if( next !== undefined ) this.tool_take( next ? 'board' : 'select' )
			return this.tool() === 'board'
		}

		override tool_text( next?: boolean ) {
			if( next !== undefined ) this.tool_take( next ? 'text' : 'select' )
			return this.tool() === 'text'
		}

		override tool_hand( next?: boolean ) {
			if( next !== undefined ) this.tool_take( next ? 'hand' : 'select' )
			return this.tool() === 'hand'
		}

		key_tools(): { readonly [ code: string ]: $bog_vmap_app_pane_tool | undefined } {
			return { KeyV: 'select', KeyF: 'board', KeyT: 'text', KeyH: 'hand' }
		}

		key_zooms(): { readonly [ code: string ]: number | undefined } {
			return { Equal: 1.25, NumpadAdd: 1.25, Minus: 1 / 1.25, NumpadSubtract: 1 / 1.25 }
		}

		key_nudges(): { readonly [ code: string ]: readonly [ number, number ] | undefined } {
			return {
				ArrowLeft: [ -1, 0 ],
				ArrowRight: [ 1, 0 ],
				ArrowUp: [ 0, -1 ],
				ArrowDown: [ 0, 1 ],
			}
		}

		nudge_near() { return 1 }

		nudge_far() { return 10 }

		nudge_note( held: readonly string[] ) {
			const rest = held.length > 1 ? ` и ещё ${ held.length - 1 }` : ''
			return `Узел ${ held[ 0 ] }${ rest } ставит раскладка родителя:`
				+ ' стрелки двигают только свободные детали.'
		}

		@ $mol_action
		nudge( way: readonly [ number, number ], step: number ) {

			const spots = { ... this.spots() } as { [ name: string ]: { readonly x: number, readonly y: number } }
			const held = [] as string[]
			let moved = 0

			for( const name of this.picked() ) {

				if( this.node_path( name ).length ) {
					held.push( name )
					continue
				}

				const spot = spots[ name ] ?? { x: 0, y: 0 }

				spots[ name ] = { x: spot.x + way[ 0 ] * step, y: spot.y + way[ 1 ] * step }
				moved += 1
			}

			if( moved ) this.spots( spots )

			this.say( held.length ? this.nudge_note( held ) : '' )

			return moved
		}

		override key_field( target: EventTarget | null ) {
			const element = target as { readonly tagName?: string, readonly isContentEditable?: boolean } | null
			if( element?.isContentEditable ) return true
			return /^(INPUT|TEXTAREA|SELECT)$/.test( element?.tagName ?? '' )
		}

		override key_down( stroke: $bog_vmap_app_pane_stroke ) {
			const field = this.key_field( stroke.target )
			const command = stroke.metaKey || stroke.ctrlKey

			if( this.menu() ) {
				this.menu( null )
				if( stroke.key === 'Escape' ) {
					stroke.preventDefault()
					return true
				}
			}

			if( stroke.key === 'Escape' ) {
				stroke.preventDefault()
				if( field ) this.focused( true )
				else this.escape()
				return true
			}

			if( field ) return false

			if( stroke.code === 'KeyD' ) {
				if( !command || stroke.altKey || stroke.shiftKey ) return false
				if( !this.editable() || !this.picked().length ) return false

				stroke.preventDefault()
				this.leave()
				this.node_copy( null )
				return true
			}

			if( stroke.key === 'Delete' || stroke.key === 'Backspace' ) {
				if( command || stroke.altKey ) return false
				if( !this.editable() ) return false

				const guide = this.guide_pick()

				if( guide && this.guide_at( guide ) ) {
					stroke.preventDefault()
					this.guide_drop( guide )
					return true
				}

				if( !this.picked().length ) return false

				stroke.preventDefault()
				this.leave()
				this.node_delete( null )
				return true
			}

			if( stroke.code === 'KeyG' && command && !stroke.altKey ) {

				if( !this.editable() ) return false

				if( stroke.shiftKey ) {
					if( !this.ungroup_enabled() ) return false

					stroke.preventDefault()
					this.leave()
					this.node_ungroup( null )
					return true
				}

				if( !this.picked().length ) return false

				stroke.preventDefault()
				this.leave()
				this.node_group( null )
				return true
			}

			if( stroke.code === 'KeyG' && command && stroke.altKey && !stroke.shiftKey ) {
				if( !this.editable() || !this.picked().length ) return false

				stroke.preventDefault()
				this.leave()
				this.node_wrap( null )
				return true
			}

			if( stroke.shiftKey && !command && !stroke.altKey ) {

				if( stroke.code === 'Digit1' ) {
					stroke.preventDefault()
					this.camera_reset()
					return true
				}

				if( stroke.code === 'Digit2' ) {
					if( !this.picked_fit() ) return false
					stroke.preventDefault()
					return true
				}

				if( stroke.code === 'Digit0' ) {
					stroke.preventDefault()
					this.zoom_to( 1 )
					return true
				}

			}

			if( stroke.code === 'Digit0' && command && !stroke.altKey && !stroke.shiftKey ) {
				stroke.preventDefault()
				this.zoom_to( 1 )
				return true
			}

			if( !command && !stroke.altKey ) {

				const mult = this.key_zooms()[ stroke.code ]
				if( mult ) {
					stroke.preventDefault()
					this.zoom_by( mult )
					return true
				}

				const way = this.key_nudges()[ stroke.code ]
				if( way ) {
					if( !this.editable() || !this.picked().length ) return false
					stroke.preventDefault()
					this.nudge( way, stroke.shiftKey ? this.nudge_far() : this.nudge_near() )
					return true
				}

			}

			if( command || stroke.altKey || stroke.shiftKey ) return false

			const tool = this.key_tools()[ stroke.code ]
			if( tool ) {
				stroke.preventDefault()
				this.tool_take( tool )
				return true
			}

			if( stroke.code === 'Space' ) {
				stroke.preventDefault()
				this.grip( true )
				return true
			}

			return false
		}

		override key_up( stroke: Pick< $bog_vmap_app_pane_stroke, 'code' > ) {
			if( stroke.code === 'Space' ) this.grip( false )
		}

		@ $mol_action
		@ $mol_action
		drag_cancel() {

			const drag = this.drag()
			if( !drag ) return null

			this.spots({ ... this.spots(), ... drag.spots })

			this.drag( null )
			this.drag_alt( false )
			this.slot( null )
			this.snap_hints( [] )

			return null
		}

		@ $mol_mem
		sizing( next?: $bog_vmap_app_pane_sizing | null ) {
			return next ?? null
		}

		handle_box( name: string, corner: string ) {

			const rect = this.part_box( name )
			if( !rect ) return null

			const x = corner.includes( 'w' ) ? rect.left
				: corner.includes( 'e' ) ? rect.left + rect.width
				: rect.left + rect.width / 2

			const y = corner.includes( 'n' ) ? rect.top
				: corner.includes( 's' ) ? rect.top + rect.height
				: rect.top + rect.height / 2

			return { x, y }
		}

		handle_at( screen: readonly [ number, number ] ) {

			if( !this.editable() ) return ''

			const picked = this.picked()
			if( picked.length !== 1 ) return ''

			const name = picked[ 0 ]
			const rect = this.part_box( name )
			if( !rect ) return ''

			const slack_x = Math.min( grab_slack, rect.width / 3 )
			const slack_y = Math.min( grab_slack, rect.height / 3 )

			if( slack_x < 1 || slack_y < 1 ) return ''

			for( const corner of corners ) {
				const spot = this.handle_box( name, corner )
				if( !spot ) continue
				if( Math.abs( screen[0] - spot.x ) > slack_x ) continue
				if( Math.abs( screen[1] - spot.y ) > slack_y ) continue
				return corner
			}

			return ''
		}

		sizing_press( corner: string, point: readonly [ number, number ], event: PointerEvent ) {

			const name = this.picked()[ 0 ]
			const box = this.part_size( name )
			if( !box ) return

			event.preventDefault()

			this.sizing({
				name,
				corner,
				box,
				grab: point,
				to: point,
				ratio: Boolean( event.shiftKey ),
			})

			try {
				this.Overlay().dom_node().setPointerCapture( event.pointerId )
			} catch {}

		}

		sizing_box( sizing = this.sizing() ): $bog_vmap_bridge_rect | null {

			if( !sizing ) return null

			const box = sizing.box
			const dx = sizing.to[0] - sizing.grab[0]
			const dy = sizing.to[1] - sizing.grab[1]

			const west = sizing.corner.includes( 'w' )
			const east = sizing.corner.includes( 'e' )
			const north = sizing.corner.includes( 'n' )
			const south = sizing.corner.includes( 's' )

			let width = box.width + ( east ? dx : west ? -dx : 0 )
			let height = box.height + ( south ? dy : north ? -dy : 0 )

			width = Math.max( size_min, Math.round( width ) )
			height = Math.max( size_min, Math.round( height ) )

			if( sizing.ratio && box.width > 0 && box.height > 0 ) {
				const ratio = box.height / box.width
				if( west || east ) height = Math.max( size_min, Math.round( width * ratio ) )
				else width = Math.max( size_min, Math.round( height / ratio ) )
			}

			return {
				x: west ? box.x + box.width - width : box.x,
				y: north ? box.y + box.height - height : box.y,
				width,
				height,
			}
		}

		sizing_floor() {

			const sizing = this.sizing()
			if( !sizing ) return null

			return this.box_union( this.node_kids( sizing.name ) )
		}

		sizing_note() {

			const sizing = this.sizing()
			const next = this.sizing_box()
			if( !sizing || !next ) return ''

			const floor = this.sizing_floor()
			const held = floor && next.height < floor.height

			const hard = this.size_hard( sizing.name )

			const width = `${ next.width }${ hard === 'width' ? ' жёстко' : '' }`
			const height = held
				? `${ Math.round( floor!.height ) } по содержимому`
				: `${ next.height }${ hard === 'height' ? ' жёстко' : '' }`

			return `${ width } × ${ height }`
		}

		size_hard( name: string ) {

			const path = this.node_path( name )
			const owner = path[ path.length - 1 ]
			if( !owner ) return ''

			return this.axis( owner ) === 'column' ? 'width' : 'height'
		}

		@ $mol_mem
		override sizing_style(): { readonly [ prop: string ]: string } {

			const box = this.sizing_box()
			if( !box ) return {}

			const rect = this.$.$bog_vmap_app_pane_screen( box, this.camera_zoom(), this.camera_shift() )

			return {
				left: rect.left + rect.width + 'px',
				top: rect.top + rect.height + 'px',
			}
		}

		sizing_cancel() {
			if( !this.sizing() ) return null
			this.sizing( null )
			return null
		}

		sizing_release() {

			const sizing = this.sizing()
			const box = this.sizing_box()

			this.sizing( null )

			if( !sizing || !box ) return
			if( box.width === sizing.box.width && box.height === sizing.box.height ) return

			this.node_resize({
				name: sizing.name,
				width: box.width,
				height: box.height,
				floor: Boolean( this.size_hard( sizing.name ) ),
			})

		}

		@ $mol_mem
		text_edited( next?: string | null ) {
			return next ?? null
		}

		text_editing( name: string ) {
			return Boolean( name ) && this.text_edited() === name
		}

		text_seed( name: string ) {
			return this.node_text( name ) || String( this.values()[ `${ name }.title` ] ?? '' )
		}

		@ $mol_mem_key
		override text_draft( name: string, next?: string ) {
			return next ?? this.text_seed( name )
		}

		@ $mol_mem_key
		override text_style( name: string ): { readonly [ prop: string ]: string } {

			const rect = this.part_box( name )
			if( !rect ) return {}

			return {
				left: rect.left + 'px',
				top: rect.top + 'px',
				width: Math.max( rect.width, 40 ) + 'px',
				height: Math.max( rect.height, 20 ) + 'px',
			}
		}

		@ $mol_action
		text_press( name: string ) {

			const kind = this.node_text_kind( name )

			if( kind === 'own' ) {

				this.text_draft( name, this.text_seed( name ) )
				this.text_edited( name )
				this.say( '' )

				const field = this.Text_field( name )
				field.selection([ 0, this.text_draft( name ).length ])
				field.bring()

				return true
			}

			if( kind ) {
				this.say(
					`Текст узла ${ name } приходит от ${ kind } и правится у источника,`
					+ ' поэтому с холста он не меняется.'
				)
				return true
			}

			return false
		}

		@ $mol_action
		override text_submit( name: string, event?: Event ) {

			if( !this.text_editing( name ) ) return null

			const draft = this.text_draft( name )

			this.text_edited( null )

			if( draft !== this.text_seed( name ) ) this.node_text_write({ name, text: draft })

			return null
		}

		@ $mol_action
		override text_key( name: string, event?: KeyboardEvent ) {

			if( event?.key !== 'Escape' ) return null

			event.stopPropagation()

			this.text_draft( name, this.text_seed( name ) )
			this.text_edited( null )

			return null
		}

		@ $mol_mem
		say( next?: string ) {
			return next ?? ''
		}

		override say_note() {
			return this.say()
		}

		@ $mol_mem
		override say_style(): { readonly [ prop: string ]: string } {

			const name = this.primary()
			const rect = name ? this.part_box( name ) : null
			if( !rect ) return {}

			return {
				left: rect.left + 'px',
				top: rect.top + rect.height + 'px',
			}
		}

		escape() {
			if( this.text_edited() ) return this.text_key( this.text_edited()!, { key: 'Escape', stopPropagation() {} } as KeyboardEvent )
			if( this.text_spot() ) return void this.text_new_key({ key: 'Escape', stopPropagation() {} } as KeyboardEvent )
			if( this.say() ) return this.say( '' )
			if( this.sizing() ) this.sizing_cancel()
			else if( this.drag() ) this.drag_cancel()
			else if( this.draft() ) this.draft( null )
			else if( this.inside() ) this.leave()
			else if( this.tool() !== 'select' ) this.tool( 'select' )
			else this.picked( [] )
		}

		@ $mol_mem
		menu( next?: $bog_vmap_app_pane_menu | null ) {
			return next ?? null
		}

		menu_key() {
			return this.menu()?.screen.join( ':' ) ?? ''
		}

		menu_view() {
			return this.Menu( this.menu_key() )
		}

		override menu_showed( next?: boolean ) {
			if( next === false ) this.menu( null )
			return Boolean( this.menu() )
		}

		override menu_left() {
			return ( this.menu()?.screen[0] ?? 0 ) + 'px'
		}

		override menu_top() {
			return ( this.menu()?.screen[1] ?? 0 ) + 'px'
		}

		override menu_on_node() {
			return Boolean( this.menu()?.name )
		}

		override node_context( event?: MouseEvent ) {
			if( !event ) return null
			if( this.carrying() ) return null

			const point = this.world_point( event )

			const held = this.inside() ? this.part_size( this.primary() ?? '' ) : null
			if( held
				&& point[0] >= held.x && point[0] <= held.x + held.width
				&& point[1] >= held.y && point[1] <= held.y + held.height
			) return null

			event.preventDefault()

			const name = this.node_at( point )

			this.leave()
			if( name && !this.picked().includes( name ) ) this.picked([ name ])

			this.menu({ screen: this.screen_point( event ), world: point, name })

			return null
		}

		parents() {
			const found = [] as string[]

			for( const name of this.picked() ) {
				const up = this.node_path( name ).at( -1 )
				if( up && !found.includes( up ) ) found.push( up )
			}

			return found
		}

		override menu_parent_enabled() {
			return this.parents().length > 0
		}

		@ $mol_action
		override menu_parent() {
			const parents = this.parents()
			if( parents.length ) this.picked( parents )
			return null
		}

		@ $mol_action
		override menu_enter() {
			const name = this.menu()?.name
			if( !name ) return null

			this.picked([ name ])
			this.enter( name )

			return null
		}

		@ $mol_action
		override menu_board() {
			const world = this.menu()?.world
			if( !world ) return null

			this.board_draw({ x: Math.round( world[0] ), y: Math.round( world[1] ), width: 0, height: 0 })

			return null
		}

		copy_gap() {
			return 24
		}

		override copy_spot( name: string ) {
			const spot = this.spots()[ name ]
			if( !spot ) return null

			const width = this.part_size( name )?.width ?? 0

			return { x: spot.x + width + this.copy_gap(), y: spot.y }
		}

		pane_rect(): $bog_vmap_app_pane_screen_box {
			const rect = this.Field().view_rect()
			if( !rect ) return { left: 0, top: 0, width: 0, height: 0 }
			return { left: rect.left, top: rect.top, width: rect.width, height: rect.height }
		}

		screen_point( event: { readonly clientX: number, readonly clientY: number } ) {
			const rect = this.pane_rect()
			return [ event.clientX - rect.left, event.clientY - rect.top ] as const
		}

		world_point( event: { readonly clientX: number, readonly clientY: number } ) {
			const screen = this.screen_point( event )
			const shift = this.camera_shift()
			const zoom = this.camera_zoom()

			return [
				( screen[0] - shift[0] ) / zoom,
				( screen[1] - shift[1] ) / zoom,
			] as const
		}

		node_at( point: readonly [ number, number ] ) {
			const slack = grab_slack / this.camera_zoom()

			let found = null as string | null
			let depth = 0

			for( const node of this.nodes_measured() ) {
				const box = node.box

				if( point[0] < box.x - slack ) continue
				if( point[1] < box.y - slack ) continue
				if( point[0] > box.x + box.width + slack ) continue
				if( point[1] > box.y + box.height + slack ) continue

				if( node.path.length < depth ) continue

				found = node.name
				depth = node.path.length

			}

			return found
		}

		node_kids( owner: string ) {
			return this.nodes_measured()
				.filter( node => node.path[ node.path.length - 2 ] === owner )
				.map( node => node.box )
		}

		container_at( point: readonly [ number, number ], moving = '' ) {
			const containers = new Set( this.containers() )

			let found = null as string | null
			let depth = 0

			for( const node of this.nodes_measured() ) {
				if( !containers.has( node.name ) ) continue
				if( node.path.length < depth ) continue

				if( moving && node.path.includes( moving ) ) continue

				const box = node.box
				if( point[0] < box.x || point[0] > box.x + box.width ) continue
				if( point[1] < box.y || point[1] > box.y + box.height ) continue

				found = node.name
				depth = node.path.length

			}

			return found
		}

		insert_slot( point: readonly [ number, number ], moving = '' ) {
			const owner = this.container_at( point, moving )
			if( !owner ) return null

			const box = this.part_size( owner )
			if( !box ) return null

			return this.$.$bog_vmap_app_pane_slot(
				owner,
				box,
				this.node_kids( owner ),
				point,
				this.axis( owner ),
			)
		}

		@ $mol_mem
		slot( next?: $bog_vmap_app_pane_slot | null ) {
			return next ?? null
		}

		override tree_move( next?: $bog_vmap_app_pane_tree_move | null ) {
			return next ?? null
		}

		override carry_drop( next?: $bog_vmap_app_pane_carry | null ) {
			return next ?? null
		}

		override files_drop( next?: $bog_vmap_app_pane_files | null ) {
			return next ?? null
		}

		override file_over( next?: Event | null ) {
			if( !this.editable() ) return null
			next?.preventDefault()
			return next ?? null
		}

		override file_take( next?: DragEvent | null ) {

			if( !next || !this.editable() ) return null

			next.preventDefault()

			const files = next.dataTransfer ? [ ... next.dataTransfer.files ] : []
			if( !files.length ) return next

			const point = this.world_point( next )
			const slot = this.insert_slot( point )

			$mol_wire_async( this ).files_drop({
				files,
				x: point[ 0 ],
				y: point[ 1 ],
				owner: slot?.owner ?? '',
				index: slot?.index ?? -1,
			})

			return next
		}

		@ $mol_action
		override carry_at( next?: { readonly x: number, readonly y: number } | null ) {
			if( !next || !this.editable() ) return null

			const slot = this.insert_slot([ next.x, next.y ])

			this.carry_drop({
				x: next.x,
				y: next.y,
				owner: slot?.owner ?? '',
				index: slot?.index ?? -1,
			})

			return next
		}

		@ $mol_mem
		band( next?: { readonly from: readonly [ number, number ], readonly to: readonly [ number, number ] } | null ) {
			return next ?? null
		}

		band_wanted( event: PointerEvent ) {
			return Boolean( event.ctrlKey || event.metaKey )
		}

		band_box() {
			const band = this.band()
			if( !band ) return null

			return {
				x: Math.min( band.from[0], band.to[0] ),
				y: Math.min( band.from[1], band.to[1] ),
				width: Math.abs( band.to[0] - band.from[0] ),
				height: Math.abs( band.to[1] - band.from[1] ),
			}
		}

		nodes_covered( box: $bog_vmap_bridge_rect ) {
			const hit = this.nodes_measured().filter( node => {
				const own = node.box
				if( own.x + own.width < box.x ) return false
				if( own.y + own.height < box.y ) return false
				if( own.x > box.x + box.width ) return false
				if( own.y > box.y + box.height ) return false
				return true
			} )

			const names = new Set( hit.map( node => node.name ) )

			return hit
				.filter( node => !node.path.slice( 0, -1 ).some( up => names.has( up ) ) )
				.map( node => node.name )
		}

		node_press( event?: PointerEvent ) {
			if( !event ) return
			if( event.button !== 0 ) return

			if( this.carrying() ) return

			if( this.hand() ) return this.press( null )

			this.say( '' )
			this.guide_pick( null )

			const point = this.world_point( event )

			const editable = this.editable()

			if( editable && ( this.tool() === 'board' || this.tool() === 'text' ) ) return this.draft_press( point, event )

			const dot = editable ? $bog_vmap_app_wire_dot_at( this.wire_dots(), this.screen_point( event ) ) : null
			if( dot ) return this.wire_press( dot, event )

			const corner = this.handle_at( this.screen_point( event ) )
			if( corner ) return this.sizing_press( corner, point, event )

			if( this.band_wanted( event ) ) {
				event.preventDefault()
				this.band({ from: point, to: point })
				this.press({ screen: [ event.clientX, event.clientY ], world: point, moved: false, entering: false, name: null })
				return
			}

			const name = this.node_at( point )

			const already = Boolean( name ) && this.picked().includes( name! )
			const entering = already && this.picked().length === 1

			if( !entering ) this.leave()
			if( !already ) this.picked( name ? [ name ] : [] )

			this.press({
				screen: [ event.clientX, event.clientY ],
				world: point,
				moved: false,
				entering,
				name,
			})

			if( !name ) return

			event.preventDefault()

			if( !editable ) return

			const spots = {} as { [ node: string ]: { readonly x: number, readonly y: number } }
			for( const picked of this.picked() ) {
				if( this.node_path( picked ).length ) continue
				spots[ picked ] = this.spots()[ picked ] ?? { x: 0, y: 0 }
			}

			this.drag({
				name,
				spots,
				grab: point,
				sizes: this.sizes(),
				nested: this.node_path( name ).length > 0,
				box: this.box_union( Object.keys( spots ).flatMap( picked => this.spot_box( picked ) ?? [] ) ),
			})

			try {
				this.Overlay().dom_node().setPointerCapture( event.pointerId )
			} catch {}

		}

		held( event: PointerEvent ) {
			if( event.buttons ) return true

			try {
				return this.Overlay().dom_node().hasPointerCapture( event.pointerId )
			} catch {
				return false
			}

		}

		press_track( event: PointerEvent ) {
			const press = this.press()
			if( !press || press.moved ) return

			const dx = event.clientX - press.screen[0]
			const dy = event.clientY - press.screen[1]

			if( Math.hypot( dx, dy ) > click_slack ) this.press({ ... press, moved: true })
		}

		@ $mol_mem
		hovered( next?: string | null ) {
			return next ?? null
		}

		hover_track( event: PointerEvent ) {
			if( this.wire_drag() || this.drag() || this.band() ) return

			const aimed = this.tool() === 'select' && !this.hand()
			this.hovered( aimed ? this.node_at( this.world_point( event ) ) : null )
		}

		@ $mol_mem
		draft( next?: $bog_vmap_app_pane_draft | null ) {
			return next ?? null
		}

		draft_press( point: readonly [ number, number ], event: PointerEvent ) {
			event.preventDefault()
			this.press( null )
			this.draft({ from: point, to: point })

			try {
				this.Overlay().dom_node().setPointerCapture( event.pointerId )
			} catch {}
		}

		draft_rect( draft: $bog_vmap_app_pane_draft ): $bog_vmap_bridge_rect {
			const left = Math.min( draft.from[0], draft.to[0] )
			const top = Math.min( draft.from[1], draft.to[1] )

			return {
				x: left,
				y: top,
				width: Math.max( draft.from[0], draft.to[0] ) - left,
				height: Math.max( draft.from[1], draft.to[1] ) - top,
			}
		}

		draft_box( draft: $bog_vmap_app_pane_draft ): $bog_vmap_bridge_rect {
			const rect = this.draft_rect( draft )

			if( Math.max( rect.width, rect.height ) * this.camera_zoom() <= click_slack ) {
				return { x: Math.round( draft.from[0] ), y: Math.round( draft.from[1] ), width: 0, height: 0 }
			}

			const x = Math.round( rect.x )
			const y = Math.round( rect.y )

			return {
				x,
				y,
				width: Math.max( 1, Math.round( rect.x + rect.width ) - x ),
				height: Math.max( 1, Math.round( rect.y + rect.height ) - y ),
			}
		}

		draft_release( draft: $bog_vmap_app_pane_draft, event: PointerEvent ) {
			const box = this.draft_box({ from: draft.from, to: this.world_point( event ) })
			const tool = this.tool()

			this.draft( null )
			this.tool( 'select' )

			try {
				this.Overlay().dom_node().releasePointerCapture( event.pointerId )
			} catch {}

			if( tool !== 'text' ) return void this.board_draw( box )

			this.text_spot({ x: box.x, y: box.y, width: box.width })
			this.text_new( '' )
			this.Text_new().bring()
		}

		@ $mol_mem
		text_spot( next?: { readonly x: number, readonly y: number, readonly width: number } | null ) {
			return next ?? null
		}

		@ $mol_mem
		override text_new( next?: string ) {
			return next ?? ''
		}

		@ $mol_mem
		override text_new_style(): { readonly [ prop: string ]: string } {

			const spot = this.text_spot()
			if( !spot ) return {}

			const rect = this.$.$bog_vmap_app_pane_screen(
				{ x: spot.x, y: spot.y, width: spot.width || this.text_new_width(), height: this.text_new_height() },
				this.camera_zoom(),
				this.camera_shift(),
			)

			return {
				left: rect.left + 'px',
				top: rect.top + 'px',
				width: Math.max( rect.width, 40 ) + 'px',
			}
		}

		text_new_width() {
			return 160
		}

		text_new_height() {
			return 24
		}

		@ $mol_action
		override text_new_submit( event?: Event ) {

			const spot = this.text_spot()
			if( !spot ) return null

			const text = this.text_new().trim()

			this.text_spot( null )
			this.text_new( '' )

			if( text ) this.text_draw({ x: spot.x, y: spot.y, width: spot.width, text })

			return null
		}

		@ $mol_action
		override text_new_key( event?: KeyboardEvent ) {

			if( event?.key !== 'Escape' ) return null
			if( !this.text_spot() ) return null

			event.stopPropagation()

			this.text_spot( null )
			this.text_new( '' )

			return null
		}

		override board_draw( next?: $bog_vmap_bridge_rect | null ) {
			return next ?? null
		}

		@ $mol_mem
		override draft_style(): { readonly [ prop: string ]: string } {
			const draft = this.draft()
			if( !draft ) return {}

			const rect = this.$.$bog_vmap_app_pane_screen(
				this.draft_rect( draft ),
				this.camera_zoom(),
				this.camera_shift(),
			)

			return {
				left: rect.left + 'px',
				top: rect.top + 'px',
				width: rect.width + 'px',
				height: rect.height + 'px',
			}
		}

		@ $mol_action
		override node_away() {
			this.hovered( null )
			return null
		}

		node_move( event?: PointerEvent ) {
			if( !event ) return
			if( this.carrying() ) return

			this.press_track( event )
			this.hover_track( event )

			const sizing = this.sizing()
			if( sizing ) {
				if( !this.held( event ) ) return this.node_release( event )
				event.preventDefault()
				this.sizing({ ... sizing, to: this.world_point( event ), ratio: Boolean( event.shiftKey ) })
				return
			}

			const draft = this.draft()
			if( draft ) {
				if( !this.held( event ) ) return this.node_release( event )
				event.preventDefault()
				this.draft({ from: draft.from, to: this.world_point( event ) })
				return
			}

			if( this.wire_drag() ) {
				if( !this.held( event ) ) return this.node_release( event )
				event.preventDefault()
				this.wire_point( this.screen_point( event ) )
				this.wire_shift( event.shiftKey )
				return
			}

			const band = this.band()
			if( band ) {
				if( !this.held( event ) ) return this.node_release( event )
				event.preventDefault()
				this.band({ from: band.from, to: this.world_point( event ) })
				return
			}

			const drag = this.drag()
			if( !drag ) return

			if( !this.held( event ) ) return this.node_release( event )

			event.preventDefault()

			const point = this.world_point( event )

			this.drag_alt( Boolean( event.altKey ) )

			const slot = this.insert_slot( point, drag.name )
			this.slot( slot )

			if( slot || drag.nested ) {
				this.snap_hints( [] )
				return
			}

			const shift = [ point[0] - drag.grab[0], point[1] - drag.grab[1] ] as const
			const snap = this.snap_off( event ) ? null : this.snap_at( drag.box, drag.spots, shift )

			this.snap_hints( snap?.lines ?? [] )

			const dx = shift[0] + ( snap?.dx ?? 0 )
			const dy = shift[1] + ( snap?.dy ?? 0 )

			const next = { ... this.spots() }

			for( const name of Object.keys( drag.spots ) ) {
				next[ name ] = {
					x: drag.spots[ name ].x + dx,
					y: drag.spots[ name ].y + dy,
				}
			}

			this.spots( next )

		}

		node_lost( event?: PointerEvent ) {
			if( !event ) return
			if( !this.sizing() && !this.draft() && !this.band() && !this.drag() && !this.wire_drag() ) return

			this.node_release( event )
		}

		node_release( event?: PointerEvent ) {
			if( !event ) return

			if( this.carrying() ) {
				const point = this.world_point( event )
				this.carry_at({ x: point[0], y: point[1] })
				return
			}

			if( this.sizing() ) {

				this.sizing_release()

				try {
					this.Overlay().dom_node().releasePointerCapture( event.pointerId )
				} catch {}

				return
			}

			const draft = this.draft()
			if( draft ) return this.draft_release( draft, event )

			const press = this.press()
			this.press( null )

			const moved = press
				? press.moved || press.name !== this.node_at( this.world_point( event ) )
				: false

			if( this.wire_drag() ) return this.wire_release( event )

			const box = this.band_box()
			if( box ) {
				this.band( null )
				if( !moved ) return

				this.leave()
				this.picked( this.nodes_covered( box ) )

				return
			}

			if( this.drag() ) {
				const drag = this.drag()
				const slot = this.slot()

				this.slot( null )

				if( drag && event.altKey ) this.drag_clone( drag, slot )
				else if( drag && slot ) this.tree_move({ names: this.drag_names( drag ), owner: slot.owner, index: slot.index })

				this.drag( null )
				this.drag_alt( false )
				this.snap_hints( [] )

				try {
					this.Overlay().dom_node().releasePointerCapture( event.pointerId )
				} catch {}

			}

			if( !press ) return
			if( event.button !== 0 ) return
			if( moved ) return

			if( press.name && this.picked().length > 1 ) return this.picked([ press.name ])

			if( !press.entering ) return

			if( press.name && this.text_press( press.name ) ) return

			this.enter( this.primary() )

			this.click_send( press.world, event )

		}

		click_send( point: readonly [ number, number ], event: PointerEvent ) {
			const target = this.target()
			if( !target ) return

			this.poke_direct( this.post( target, {
				kind: 'click_at',
				x: point[0],
				y: point[1],
				mods: {
					altKey: Boolean( event.altKey ),
					ctrlKey: Boolean( event.ctrlKey ),
					metaKey: Boolean( event.metaKey ),
					shiftKey: Boolean( event.shiftKey ),
				},
			} ) )

		}

		override frames() {
			return this.picked().filter( name => this.part_box( name ) )
		}

		frame_showed() {
			return this.frames().length > 0
		}

		frame_box(): $bog_vmap_app_pane_screen_box | null {
			const name = this.primary()
			return name ? this.part_box( name ) : null
		}

		@ $mol_mem_key
		part_box( name: string ): $bog_vmap_app_pane_screen_box | null {
			const box = this.part_size( name )
			if( !box ) return null

			const drag = this.drag()
			const spot = this.spots()[ name ]
			const start = drag?.spots[ name ]

			const live = start && spot && this.sizes() === drag!.sizes
			const dx = live ? spot.x - start.x : 0
			const dy = live ? spot.y - start.y : 0

			return this.$.$bog_vmap_app_pane_screen(
				{ x: box.x + dx, y: box.y + dy, width: box.width, height: box.height },
				this.camera_zoom(),
				this.camera_shift(),
			)
		}

		@ $mol_mem
		override insert_style(): { readonly [ prop: string ]: string } {
			const slot = this.slot()
			if( !slot ) return {}

			const rect = this.$.$bog_vmap_app_pane_screen(
				slot.line,
				this.camera_zoom(),
				this.camera_shift(),
			)

			return {
				left: rect.left + 'px',
				top: rect.top + 'px',
				width: Math.max( rect.width, 2 ) + 'px',
				height: Math.max( rect.height, 2 ) + 'px',
			}
		}

		@ $mol_mem
		override band_style(): { readonly [ prop: string ]: string } {
			const box = this.band_box()
			if( !box ) return {}

			const rect = this.$.$bog_vmap_app_pane_screen(
				box,
				this.camera_zoom(),
				this.camera_shift(),
			)

			return {
				left: rect.left + 'px',
				top: rect.top + 'px',
				width: rect.width + 'px',
				height: rect.height + 'px',
			}
		}

		inner_shown() {
			const inner = this.inner()
			return inner && this.part_box( inner ) ? inner : ''
		}

		@ $mol_mem
		override inner_style(): { readonly [ prop: string ]: string } {

			const rect = this.part_box( this.inner() )
			if( !rect ) return {}

			return {
				left: rect.left + 'px',
				top: rect.top + 'px',
				width: rect.width + 'px',
				height: rect.height + 'px',
			}
		}

		@ $mol_mem_key
		override frame_style( name: string ): { readonly [ prop: string ]: string } {

			const sizing = this.sizing()
			const box = sizing?.name === name ? this.sizing_box() : null

			const rect = box
				? this.$.$bog_vmap_app_pane_screen( box, this.camera_zoom(), this.camera_shift() )
				: this.part_box( name )

			if( !rect ) return {}

			return {
				left: rect.left + 'px',
				top: rect.top + 'px',
				width: rect.width + 'px',
				height: rect.height + 'px',
			}
		}

		@ $mol_mem
		override overlay_style(): { readonly [ prop: string ]: string } {
			const rect = !this.carrying() && !this.hand() && this.inside() ? this.frame_box() : null
			return { clipPath: this.$.$bog_vmap_app_pane_hole( rect ) }
		}

		@ $mol_mem
		drag_alt( next?: boolean ) {
			return next ?? false
		}

		ghost_names() {

			const drag = this.drag()
			if( !drag || !this.drag_alt() ) return []

			return Object.keys( drag.spots )
		}

		ghost_views() {
			return this.ghost_names().map( name => this.Ghost( name ) )
		}

		@ $mol_mem_key
		override ghost_style( name: string ): { readonly [ prop: string ]: string } {

			const spot = this.drag()?.spots[ name ]
			const size = this.part_size( name )

			if( !spot || !size ) return {}

			const rect = this.$.$bog_vmap_app_pane_screen(
				{ x: spot.x, y: spot.y, width: size.width, height: size.height },
				this.camera_zoom(),
				this.camera_shift(),
			)

			return {
				left: rect.left + 'px',
				top: rect.top + 'px',
				width: rect.width + 'px',
				height: rect.height + 'px',
			}
		}

		@ $mol_action
		drag_names(
			drag: {
				readonly name: string
				readonly spots: { readonly [ name: string ]: { readonly x: number, readonly y: number } }
			},
		) {
			return drag.spots[ drag.name ] ? Object.keys( drag.spots ) : [ drag.name ]
		}

		drag_clone(
			drag: {
				readonly name: string
				readonly nested: boolean
				readonly spots: { readonly [ name: string ]: { readonly x: number, readonly y: number } }
			},
			slot: $bog_vmap_app_pane_slot | null,
		) {

			const dropped = this.spots()

			if( slot ) {
				this.spots({ ... dropped, ... drag.spots })
				this.node_clone({ names: this.drag_names( drag ), owner: slot.owner, index: slot.index })
				return null
			}

			if( drag.nested ) {
				this.node_clone({ names: [ drag.name ] })
				return null
			}

			const points = {} as { [ name: string ]: { readonly x: number, readonly y: number } }

			for( const name of Object.keys( drag.spots ) ) {
				const spot = dropped[ name ]
				if( spot ) points[ name ] = spot
			}

			if( !Object.keys( points ).length ) return null

			this.spots({ ... dropped, ... drag.spots })
			this.node_clone({ names: Object.keys( points ), spots: points })

			return null
		}

		override link_add( next?: $bog_vmap_app_pane_link_new | null ) {
			return next ?? null
		}

		override link_drop( next?: $bog_vmap_app_pane_link_end | null ) {
			return next ?? null
		}

		@ $mol_mem
		wire_drag( next?: { from: string, from_prop: string, kind: $bog_vmap_app_inspect_value_kind } | null ) {
			return next ?? null
		}

		@ $mol_mem
		wire_point( next?: readonly [ number, number ] ) {
			return next ?? [ 0, 0 ] as const
		}

		@ $mol_mem
		wire_shift( next?: boolean ) {
			return next ?? false
		}

		wire_source_next() {
			const drag = this.wire_drag()
			if( !drag ) return false

			return this.part_ports( drag.from ).find( port => port.name === drag.from_prop )?.next ?? false
		}

		wire_bidi() {
			return this.wire_shift() && this.wire_source_next()
		}

		wire_hinted( dots: readonly $bog_vmap_app_wire_dot[] ) {
			if( this.wire_shift() ) return dots
			if( !this.wire_source_next() ) return dots

			const aimed = $bog_vmap_app_wire_dot_at( dots, this.wire_point() )
			if( !aimed || !aimed.lit || !aimed.port.next ) return dots

			return dots.map(
				dot => dot === aimed ? { ... dot, hint: $bog_vmap_app_wire_hint } : dot
			)
		}

		@ $mol_mem_key
		part_dots( name: string ): readonly $bog_vmap_app_wire_port[] {
			const written = new Set( this.part_overs( name ) )

			for( const link of this.wires() ) {
				if( link.to === name ) written.add( link.to_prop )
			}

			const ports = this.part_ports( name ).filter(
				port => ( port.own || written.has( port.name ) ) && $bog_vmap_app_wire_plain( port )
			)

			let spare = false

			return ports.filter( port => {

				if( !$bog_vmap_app_wire_slot( port ) || written.has( port.name ) ) return true

				if( spare ) return false

				spare = true

				return true
			} )
		}

		part_lift( name: string ) {

			const box = this.part_box( name )
			if( !box ) return 0

			return $bog_vmap_app_wire_lift( box, this.part_dots( name ).length, this.pane_rect().height )
		}

		wire_over() {
			if( !this.wire_drag() ) return null

			const point = this.wire_point()

			let own = null as string | null
			let near = null as string | null
			let best = Infinity

			for( const name of this.part_names() ) {

				const box = this.part_box( name )
				if( !box ) continue

				if( $bog_vmap_app_wire_over( box, point ) ) {
					own = name
					continue
				}

				const count = this.part_dots( name ).length
				const lift = this.part_lift( name )

				if( !$bog_vmap_app_wire_over( box, point, count, lift ) ) continue

				const off = $bog_vmap_app_wire_off( box, point, count, lift )
				if( off > best ) continue

				best = off
				near = name

			}

			return own ?? near
		}

		part_spread( name: string ) {
			return name === this.primary() || name === this.hovered() || name === this.wire_over()
		}

		port_index( name: string, port: string ) {
			return Math.max( 0, this.part_dots( name ).findIndex( known => known.name === port ) )
		}

		port_point( name: string, port: string, side: $bog_vmap_app_wire_side ) {
			const box = this.part_box( name )
			if( !box ) return null

			return this.part_spread( name )
				? $bog_vmap_app_wire_port_point( box, side, this.port_index( name, port ), this.part_lift( name ) )
				: $bog_vmap_app_wire_side_point( box, side )
		}

		@ $mol_mem
		override wire_lines(): readonly $bog_vmap_app_wire_line[] {
			const values = this.values()
			const lines = [] as $bog_vmap_app_wire_line[]

			for( const link of this.wires() ) {
				const from = this.port_point( link.from, link.from_prop, 'out' )
				const to = this.port_point( link.to, link.to_prop, 'in' )
				if( !from || !to ) continue

				const mid = $bog_vmap_app_wire_curve_mid( from, to )

				lines.push({
					key: `${ link.to }.${ link.to_prop }`,
					geometry: $bog_vmap_app_wire_curve( from, to ),
					label: String( values[ link.name ] ?? '' ),
					label_x: mid[0],
					label_y: mid[1],
					bidi: link.bidi,
				})

			}

			return lines
		}

		@ $mol_mem
		override wire_dots(): readonly $bog_vmap_app_wire_dot[] {
			const linked = new Set( this.wires().map( link => `${ link.to }.${ link.to_prop }` ) )
			const dots = [] as $bog_vmap_app_wire_dot[]

			const add = (
				node: string,
				side: $bog_vmap_app_wire_side,
				lit: ( port: $bog_vmap_app_wire_port )=> boolean,
				keep: ( port: $bog_vmap_app_wire_port )=> boolean = ()=> true,
			)=> {
				const box = this.part_box( node )
				if( !box ) return

				const ports = this.part_dots( node )

				const mark = ( port: $bog_vmap_app_wire_port, x: number, y: number )=> dots.push({
					node, port, side, x, y,
					hint: '',
					lit: lit( port ),
					linked: side === 'in' && linked.has( `${ node }.${ port.name }` ),
				})

				if( this.part_spread( node ) ) {

					const lift = this.part_lift( node )

					ports.forEach( ( port, index )=> {
						if( !keep( port ) ) return
						const [ x, y ] = $bog_vmap_app_wire_port_point( box, side, index, lift )
						mark( port, x, y )
					} )

					return
				}

				const aimed = ports.findIndex( port => keep( port ) && lit( port ) )
				const index = aimed < 0 ? ports.findIndex( keep ) : aimed
				const port = index < 0 ? null : ports[ index ]
				if( !port ) return

				const [ x, y ] = $bog_vmap_app_wire_port_point( box, side, index )
				mark( port, x, y )

			}

			const drag = this.wire_drag()

			if( drag ) {
				for( const name of this.part_names() ) {
					if( name === drag.from ) continue
					add( name, 'in', port => $bog_vmap_app_wire_takes( drag.kind, port, this.wire_bidi() ) )
				}
				return this.wire_hinted( dots )
			}

			const shown = [ this.primary(), this.hovered() ].filter( Boolean ) as readonly string[]

			for( const name of new Set( shown ) ) {
				add( name, 'in', ()=> true, port => linked.has( `${ name }.${ port.name }` ) )
				add( name, 'out', ()=> true )
			}

			return dots
		}

		override wire_drag_geometry() {
			const drag = this.wire_drag()
			if( !drag ) return ''

			const from = this.port_point( drag.from, drag.from_prop, 'out' )
			if( !from ) return ''

			return $bog_vmap_app_wire_curve( from, this.wire_point() )
		}

		wire_press( dot: $bog_vmap_app_wire_dot, event: PointerEvent ) {
			event.preventDefault()
			this.press( null )

			let source = { from: dot.node, from_prop: dot.port.name, kind: dot.port.kind }

			if( dot.side === 'in' ) {
				const link = this.wires().find( link => link.to === dot.node && link.to_prop === dot.port.name )
				if( !link ) return

				const port = this.part_ports( link.from ).find( port => port.name === link.from_prop )
				source = { from: link.from, from_prop: link.from_prop, kind: port?.kind ?? 'null' }

				this.link_drop({ to: link.to, to_prop: link.to_prop })

			}

			this.wire_point( this.screen_point( event ) )
			this.wire_drag( source )
			this.wire_shift( event.shiftKey )

			try {
				this.Overlay().dom_node().setPointerCapture( event.pointerId )
			} catch {}

		}

		wire_release( event: PointerEvent ) {
			const drag = this.wire_drag()!
			const point = this.wire_point( this.screen_point( event ) )
			const bidi = this.wire_bidi()
			const dot = $bog_vmap_app_wire_dot_at( this.wire_dots(), point )

			this.wire_drag( null )
			this.wire_shift( false )

			try {
				this.Overlay().dom_node().releasePointerCapture( event.pointerId )
			} catch {}

			if( !dot || !dot.lit ) return

			this.link_add({
				from: drag.from,
				from_prop: drag.from_prop,
				to: dot.node,
				to_prop: dot.port.name,
				bidi,
			})

		}

		@ $mol_mem
		wires_visible(): readonly string[] {
			const rect = this.pane_rect()
			const names = new Set< string >()

			for( const link of this.wires() ) {
				const from = this.port_point( link.from, link.from_prop, 'out' )
				const to = this.port_point( link.to, link.to_prop, 'in' )
				if( !from || !to ) continue

				if( Math.max( from[0], to[0] ) < 0 ) continue
				if( Math.max( from[1], to[1] ) < 0 ) continue
				if( Math.min( from[0], to[0] ) > rect.width ) continue
				if( Math.min( from[1], to[1] ) > rect.height ) continue

				names.add( link.name )

			}

			return [ ... names ]
		}

		board( name: string ) {
			return this.free_names().includes( name ) && this.containers().includes( name )
		}

		@ $mol_mem_key
		part_outs( name: string ): readonly $bog_vmap_app_wire_port[] {
			const fed = new Set(
				this.wires().filter( link => link.to === name ).map( link => link.to_prop )
			)

			const written = new Set( this.part_overs( name ) )

			return this.part_ports( name ).filter(
				port => written.has( port.name )
					&& !fed.has( port.name )
					&& $bog_vmap_app_wire_plain( port )
					&& port.name !== 'title'
			)
		}

		part_shown( name: string ) {
			const box = this.part_box( name )
			if( !box ) return false

			const rect = this.pane_rect()

			return box.left + box.width >= 0
				&& box.top + box.height >= 0
				&& box.left <= rect.width
				&& box.top <= rect.height
		}

		@ $mol_mem
		parts_visible(): readonly string[] {
			return this.free_names().filter( name => this.part_shown( name ) )
		}

		@ $mol_mem
		ports_visible(): readonly string[] {
			const names = [] as string[]

			for( const name of this.parts_visible() ) {
				for( const port of this.part_outs( name ) ) names.push( name + '.' + port.name )
			}

			return names
		}

		@ $mol_mem_key
		override label_lines( name: string ): readonly string[] {
			const values = this.values()
			const lines = [] as string[]

			for( const port of this.part_outs( name ) ) {

				const text = String( values[ name + '.' + port.name ] ?? '' )
				if( !text ) continue

				if( !text.includes( '\n' ) ) {
					lines.push( port.name + ': ' + text )
					continue
				}

				lines.push( port.name )
				for( const line of text.split( '\n' ) ) lines.push( line )

			}

			return lines
		}

		@ $mol_mem_key
		override label_style( name: string ): { readonly [ prop: string ]: string } {
			const box = this.part_box( name )
			if( !box ) return {}

			return {
				left: box.left + 'px',
				top: ( box.top + box.height ) + 'px',
			}
		}

		@ $mol_mem
		override value_labels(): readonly $mol_view[] {
			if( !this.warmed() ) return []

			try {
				return this.parts_visible()
					.filter( name => this.label_lines( name ).length )
					.map( name => this.Label( name ) )
			} catch( error: unknown ) {
				if( $mol_promise_like( error ) ) return $mol_fail_hidden( error )
				return []
			}
		}

		@ $mol_mem
		override name_views(): readonly $mol_view[] {
			return this.parts_visible()
				.filter( name => this.board( name ) )
				.map( name => this.name_editing( name ) ? this.Name_field( name ) : this.Name( name ) )
		}

		@ $mol_mem_key
		override name_style( name: string ): { readonly [ prop: string ]: string } {
			const box = this.part_box( name )
			if( !box ) return {}

			return {
				left: box.left + 'px',
				top: box.top + 'px',
			}
		}

		override name_title( name: string ) {
			return name
		}

		override name_picked( name: string ) {
			return this.picked().includes( name )
		}

		@ $mol_mem
		name_edited( next?: string | null ) {
			return next ?? null
		}

		name_editing( name: string ) {
			return Boolean( name ) && this.name_edited() === name
		}

		@ $mol_action
		override name_press( name: string, event?: Event ) {
			if( !event ) return null

			this.leave()
			this.picked([ name ])

			return null
		}

		@ $mol_action
		override name_edit( name: string, event?: Event ) {
			if( !event || !this.editable() ) return null

			this.picked([ name ])
			this.name_draft( name, name )
			this.name_edited( name )

			this.Name_field( name ).selection([ 0, name.length ])

			return null
		}

		@ $mol_mem_key
		override name_draft( name: string, next?: string ) {
			return next ?? name
		}

		@ $mol_action
		override name_submit( name: string, event?: Event ) {
			if( !this.name_editing( name ) ) return null

			const draft = this.name_draft( name )

			if( !draft || draft === name ) {
				this.name_edited( null )
				return null
			}

			if( this.node_title( draft ) === draft ) this.name_edited( null )

			return null
		}

		@ $mol_action
		override name_key( name: string, event?: KeyboardEvent ) {
			if( event?.key !== 'Escape' ) return null

			event.stopPropagation()

			this.name_draft( name, name )
			this.name_edited( null )

			return null
		}

		@ $mol_mem
		values_push() {
			const target = this.target()
			const names = [ ... this.wires_visible(), ... this.ports_visible() ]
			if( !target ) return names

			this.$.$bog_vmap_bridge_send( target, { kind: 'values_want', names } )

			return names
		}

		post( target: { postMessage( data: unknown, origin: string ): void }, message: $bog_vmap_bridge_down ) {
			this.$.$bog_vmap_bridge_send( target, message )
			return this.poke_at = this.stamp()
		}

		@ $mol_mem
		pack_push() {
			const target = this.target()
			const uri = this.pack_uri()
			if( !target ) return uri

			this.post( target, { kind: 'pack_set', uri } )

			return uri
		}

		@ $mol_mem
		theme_push() {
			const target = this.target()
			const theme = this.theme()
			if( !target ) return theme

			this.post( target, { kind: 'theme_set', theme } )

			return theme
		}

		@ $mol_mem
		doc_push() {
			const target = this.target()
			const src = this.doc_src()
			if( !target ) return src

			this.post( target, {
				kind: 'doc_set',
				src,
				js: this.doc_js(),
				root: this.doc_root(),
			} )

			return src
		}

		@ $mol_mem
		css_push() {
			const target = this.target()
			const css = this.doc_css()
			if( !target ) return css

			this.post( target, { kind: 'css_set', css } )

			return css
		}

		@ $mol_mem
		spots_push() {
			const target = this.target()
			const spots = this.spots()
			if( !target ) return spots

			this.post( target, { kind: 'spots_set', spots } )

			return spots
		}

		@ $mol_mem
		libs_push() {
			const target = this.target()
			const parts = this.libs()
			if( !target ) return parts

			this.post( target, { kind: 'libs_set', parts } )

			return parts
		}

		@ $mol_mem
		camera_push() {
			const target = this.target()
			const camera = this.camera()
			if( !target ) return camera

			this.post( target, { kind: 'camera_set', camera } )

			return camera
		}

		@ $mol_mem
		override isolation() {
			if( !this.ready() ) return ''

			const peer = this.scene_peer()
			if( !peer ) return 'Кадра сцены нет — рисовать документ негде'

			try {
				const origin = peer.origin
				return `Песочница не работает: кадр сцены живёт на origin ${ origin }, то есть код документа исполняется наравне с редактором`
			} catch {
				return ''
			}

		}

		message_receive( event?: MessageEvent ) {
			if( !event ) return

			const peer = this.scene_peer()
			if( !peer ) return

			const message = this.$.$bog_vmap_bridge_read< $bog_vmap_bridge_up >( event, peer )
			if( !message ) return

			const kind: string = message.kind

			if( kind === 'boot_fail' ) {
				this.scene_lost( this.scene_bundle() )
				return
			}

			this.answer_at( this.stamp() )
			this.stalled( false )
			this.scene_lost( '' )

			if( message.kind === 'ready' ) {
				this.error_at( 'compile', '' )
				this.error_at( 'runtime', '' )
				this.error_at( 'pack', '' )

				this.warmed( false )

				const key = this.scene_key()
				this.handshake( key, this.handshake( key ) + 1 )
				return
			}

			if( message.kind === 'error' ) {
				const at = message.at === 'compile' ? 'compile'
					: message.at === 'pack' ? 'pack'
					: 'runtime'

				if( message.message === null ) {
					this.error_at( at, '' )
					return
				}

				if( at === 'pack' ) {
					this.error_at( at, message.message )
					return
				}

				const label = at === 'compile' ? 'компиляция' : 'исполнение'
				const node = message.node ? ` — ${ message.node }` : ''
				this.error_at( at, `${ label }${ node }: ${ message.message }` )
				this.error_node( at, message.node ?? '' )
				return
			}

			if( message.kind === 'key' ) {
				this.escape()
				return
			}

			if( message.kind === 'values' ) {
				this.values( message.values )
				return
			}

			if( message.kind === 'sizes' ) {
				this.sizes( this.sizes_merged( message.sizes ) )
				this.warmed( true )
				this.restart_tries( 0 )
				this.camera_settle()
				return
			}

		}

		@ $mol_mem
		message_listener() {
			return new this.$.$mol_dom_listener(
				this.$.$mol_dom_context,
				'message',
				$mol_wire_async( this ).message_receive,
			)
		}

		override auto() {
			return [
				... super.auto(),
				this.view_rect(),
				this.message_listener(),
				this.pack_push(),
				this.theme_push(),
				this.doc_push(),
				this.css_push(),
				this.libs_push(),
				this.spots_push(),
				this.camera_push(),
				this.camera_keep(),
				this.values_push(),
				this.heartbeat(),
				this.watchdog(),
			]
		}

	}

	export class $bog_vmap_app_pane_label extends $.$bog_vmap_app_pane_label {

		line( index: string ) {
			return this.lines()[ Number( index ) ] ?? ''
		}

		@ $mol_mem
		override rows() {
			return this.lines().map( ( line, index )=> this.Row( String( index ) ) )
		}

		@ $mol_mem_key
		override row_cells( index: string ) {
			return this.line( index ).split( '\t' ).map( ( cell, at )=> this.Cell( index + '/' + at ) )
		}

		override cell_text( key: string ) {
			const [ index, at ] = key.split( '/' )
			return this.line( index ).split( '\t' )[ Number( at ) ] ?? ''
		}

	}

	export class $bog_vmap_app_pane_overlay extends $.$bog_vmap_app_pane_overlay {
		override sub() {
			return [
				... this.frames().map( name => this.Frame( name ) ),
				... this.inner() ? [ this.Inner() ] : [],
			]
		}

	}

}
